import { useEffect, useState } from 'react';
import {
  getAttendanceOverview,
  getAttendanceClassReport,
  getAttendanceStudentReport,
} from '../../services/api';
import { formatTime } from '../../utils/timeUtils';

// One rate threshold for every screen: green is healthy, amber is drifting,
// red is the churn-risk conversation Amrit wants to have early.
const pctColor = (pct) =>
  pct === null ? 'text-gray-400' : pct >= 85 ? 'text-green-600' : pct >= 70 ? 'text-amber-600' : 'text-red-600';

const classTitle = (cls) => cls.name || `${cls.type} — ${cls.ageGroup}`;

const longDate = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const StatusIcon = ({ status }) => {
  if (status === 'present') {
    return (
      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  if (status === 'absent') {
    return (
      <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" strokeWidth={2} />
      <path strokeLinecap="round" strokeWidth={2} d="M12 7v5l3 2" />
    </svg>
  );
};

const TotalsChips = ({ present, absent, late }) => (
  <span className="flex items-center gap-1.5 text-xs font-medium">
    <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-700 ring-1 ring-green-200">
      {present} P
    </span>
    <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-600 ring-1 ring-red-200">
      {absent} A
    </span>
    <span className="rounded-full bg-sky-50 px-2 py-0.5 text-sky-600 ring-1 ring-sky-200">
      {late} L
    </span>
  </span>
);

/** All classes, lowest attendance first — the list to scan for churn risk. */
const Overview = ({ onOpenClass }) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getAttendanceOverview()
      .then((res) => setReport(res.data))
      .catch(() => setError('Could not load the report — try refreshing the page'));
  }, []);

  if (error) return <div className="mx-auto max-w-2xl rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  if (!report) return <div className="py-10 text-center text-sm text-gray-400">Loading…</div>;

  const withData = report.classes.filter((c) => c.pct !== null);
  const noData = report.classes.filter((c) => c.pct === null);

  return (
    <div className="mx-auto max-w-2xl">
      {withData.length === 0 ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200">
          No attendance has been marked yet — reports appear once classes are marked.
        </div>
      ) : (
        <>
          <p className="mb-2 text-xs text-gray-400">
            Lowest attendance first — the classes to check on.
          </p>
          <div className="divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
            {withData.map((cls) => (
              <button
                key={cls.id}
                onClick={() => onOpenClass(cls)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-800">{classTitle(cls)}</div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {[cls.day, formatTime(cls.startTime), cls.instructor].filter(Boolean).join(' · ')}
                    {` · ${cls.sessions} ${cls.sessions === 1 ? 'session' : 'sessions'}`}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <TotalsChips present={cls.present} absent={cls.absent} late={cls.late} />
                  <span className={`w-12 text-right text-base font-bold ${pctColor(cls.pct)}`}>
                    {cls.pct}%
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      {noData.length > 0 && (
        <p className="mt-4 text-center text-xs text-gray-400">
          {noData.length} {noData.length === 1 ? 'class has' : 'classes have'} no marked sessions yet.
        </p>
      )}
    </div>
  );
};

/** Weekly bars — direction of travel at a glance, no chart library needed. */
const WeeklyTrend = ({ weekly }) => {
  if (weekly.length < 2) return null;
  return (
    <div className="mb-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
        Weekly trend
      </h3>
      <div className="flex items-end gap-2">
        {weekly.map((week) => (
          <div key={week.weekStart} className="flex flex-1 flex-col items-center gap-1">
            <span className={`text-xs font-semibold ${pctColor(week.pct)}`}>{week.pct}%</span>
            <div className="flex h-16 w-full items-end rounded bg-gray-50">
              <div
                className={`w-full rounded ${
                  week.pct >= 85 ? 'bg-green-500' : week.pct >= 70 ? 'bg-amber-400' : 'bg-red-400'
                }`}
                style={{ height: `${Math.max(week.pct, 4)}%` }}
              />
            </div>
            <span className="text-[10px] text-gray-400">
              {longDate(week.weekStart).replace(/^\w+, /, '')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClassReport = ({ cls, onBack, onOpenStudent }) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getAttendanceClassReport(cls.id)
      .then((res) => setReport(res.data))
      .catch(() => setError('Could not load this class — go back and try again'));
  }, [cls.id]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={onBack} className="mb-3 text-sm text-gray-500 hover:text-gray-700">← Back</button>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </div>
    );
  }
  if (!report) return <div className="py-10 text-center text-sm text-gray-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-2xl">
      <button onClick={onBack} className="mb-1 text-sm text-gray-500 hover:text-gray-700">
        ← All classes
      </button>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{classTitle(report.class)}</h2>
          <p className="text-sm text-gray-500">
            {[report.class.day, formatTime(report.class.startTime), report.class.instructor]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${pctColor(report.pct)}`}>
            {report.pct === null ? '—' : `${report.pct}%`}
          </div>
          <div className="text-xs text-gray-400">
            avg over {report.sessions.length}{' '}
            {report.sessions.length === 1 ? 'session' : 'sessions'}
          </div>
        </div>
      </div>

      <WeeklyTrend weekly={report.weekly} />

      <div className="mb-4 divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
          <span>Student</span>
          <span>P / A / L · rate</span>
        </div>
        {report.students.map((student) => (
          <button
            key={student.studentKey}
            onClick={() => onOpenStudent(student)}
            className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50"
          >
            <span
              className={`truncate text-sm font-medium ${
                student.onRoster ? 'text-gray-800' : 'text-gray-400'
              }`}
            >
              {student.name}
              {!student.onRoster && ' (left class)'}
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <TotalsChips present={student.present} absent={student.absent} late={student.late} />
              <span className={`w-12 text-right text-sm font-bold ${pctColor(student.pct)}`}>
                {student.pct === null ? '—' : `${student.pct}%`}
              </span>
            </span>
          </button>
        ))}
      </div>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        Sessions
      </h3>
      <div className="divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {report.sessions.map((session) => (
          <div key={session.date} className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-gray-700">{longDate(session.date)}</span>
            <TotalsChips
              present={session.summary.present}
              absent={session.summary.absent}
              late={session.summary.late}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const StudentReport = ({ student, onBack }) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getAttendanceStudentReport(student.studentKey)
      .then((res) => setReport(res.data))
      .catch(() => setError('Could not load this student — go back and try again'));
  }, [student.studentKey]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={onBack} className="mb-3 text-sm text-gray-500 hover:text-gray-700">← Back</button>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </div>
    );
  }
  if (!report) return <div className="py-10 text-center text-sm text-gray-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-2xl">
      <button onClick={onBack} className="mb-1 text-sm text-gray-500 hover:text-gray-700">
        ← Back
      </button>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{report.name}</h2>
          <p className="text-sm text-gray-500">
            {report.marked} marked {report.marked === 1 ? 'session' : 'sessions'}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${pctColor(report.pct)}`}>
            {report.pct === null ? '—' : `${report.pct}%`}
          </div>
          <div className="mt-1">
            <TotalsChips present={report.present} absent={report.absent} late={report.late} />
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {report.history.map((item) => (
          <div key={`${item.date}-${item.className}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <div className="text-sm text-gray-700">{longDate(item.date)}</div>
              <div className="truncate text-xs text-gray-400">
                {item.className}
                {item.startTime && ` · ${formatTime(item.startTime)}`}
              </div>
            </div>
            <StatusIcon status={item.status} />
          </div>
        ))}
        {report.history.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">No marked sessions yet.</div>
        )}
      </div>
    </div>
  );
};

const AttendanceReports = () => {
  const [openClass, setOpenClass] = useState(null);
  const [openStudent, setOpenStudent] = useState(null);

  if (openStudent) {
    return <StudentReport student={openStudent} onBack={() => setOpenStudent(null)} />;
  }
  if (openClass) {
    return (
      <ClassReport
        cls={openClass}
        onBack={() => setOpenClass(null)}
        onOpenStudent={setOpenStudent}
      />
    );
  }
  return <Overview onOpenClass={setOpenClass} />;
};

export default AttendanceReports;
